"use client";


import type { NavigationLink } from "@components/header/types";
import type { HeaderFragment, HeaderStoreFragment } from "@server/gql/graphql";
import { useScroll } from "@server/hooks";
import type { ScrollDirection } from "@server/hooks/useScroll";
import type { PropsWithChildren } from "react";
import { createContext, useContext, useMemo } from "react";

import { getDefaultMarket } from "@/config/market-configurations";

type HeaderContextProps = {
  locale?: string;
  leftLinksCollection?: HeaderFragment["leftLinksCollection"];
  rightLinksCollection?: HeaderFragment["rightLinksCollection"];
  stores?: HeaderStoreFragment[];
  services?: HeaderStoreFragment[];
  navigation: NavigationLink[];
  contactNavigation: NavigationLink[];
  scrollDirection?: ScrollDirection;
  scrollPosition?: number;
  hasScrolled?: boolean;
};

export const HeaderContext = createContext<HeaderContextProps>({
  navigation: [],
  contactNavigation: [],
});

export const HeaderContextProvider = ({
  locale = getDefaultMarket().locale,
  leftLinksCollection,
  rightLinksCollection,
  stores,
  services,
  children,
  navigation,
  contactNavigation,
}: PropsWithChildren<HeaderContextProps>) => {
  const { scrollDirection, scrollPosition } = useScroll();
  const hasScrolled = useMemo(() => scrollPosition > 120, [scrollPosition]);

  const state = useMemo(
    () => ({
      locale,
      leftLinksCollection,
      rightLinksCollection,
      stores,
      services,
      navigation,
      contactNavigation,
      scrollDirection,
      scrollPosition,
      hasScrolled,
    }),
    [
      locale,
      leftLinksCollection,
      rightLinksCollection,
      stores,
      services,
      navigation,
      contactNavigation,
      scrollDirection,
      scrollPosition,
      hasScrolled,
    ],
  );
  return (
    <HeaderContext.Provider value={state}>{children}</HeaderContext.Provider>
  );
};

export const useHeaderContext = () => {
  const context = useContext(HeaderContext);

  if (context === undefined) {
    throw new Error(
      "useHeaderContext must be used within a HeaderContextProvider",
    );
  }

  return context;
};
